import { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import {BookOpen, Plus, Edit2, Trash2, Search, X, Users, Calendar} from 'lucide-react'

interface BibleSchoolClass {
  _id: string
  title: string
  category: 'ADULTS' | 'YOUTH' | 'KIDS'
  teacher: string
  schedule: string
  description?: string
  startDate?: string
  endDate?: string
  maxStudents?: number
  materialsUrl?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export default function BibleSchoolManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<'ALL' | 'ADULTS' | 'YOUTH' | 'KIDS'>('ALL')
  const [isCreating, setIsCreating] = useState(false)
  const [editingClass, setEditingClass] = useState<BibleSchoolClass | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    category: 'ADULTS' as 'ADULTS' | 'YOUTH' | 'KIDS',
    teacher: '',
    schedule: '',
    description: '',
    startDate: '',
    endDate: '',
    maxStudents: 0,
    materialsUrl: ''
  })

  const { data: classes, loading, fetchData, createRecord, updateRecord, deleteRecord } = useEntityCRUD<BibleSchoolClass>('bible_school_classes')
  const { logAction } = useAdminLog()

  useEffect(() => {
    fetchData()
  }, [])

  const filteredClasses = classes.filter(cls => {
    const matchesSearch = cls.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cls.teacher.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === 'ALL' || cls.category === filterCategory
    return matchesSearch && matchesCategory
  })

  const getCategoryLabel = (category: string) => {
    const labels = { ADULTS: 'Adultos', YOUTH: 'Jovens', KIDS: 'Crianças' }
    return labels[category as keyof typeof labels] || category
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      ADULTS: 'bg-blue-100 text-blue-800',
      YOUTH: 'bg-purple-100 text-purple-800',
      KIDS: 'bg-green-100 text-green-800'
    }
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await createRecord({
        ...formData,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await logAction('CREATE', 'bible_school_classes', '', `Turma criada: ${formData.title}`)
      resetForm()
      fetchData()
    } catch (error) {
      console.error('Erro ao criar turma:', error)
      alert('Erro ao criar turma')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingClass) return
    try {
      await updateRecord(editingClass._id, {
        ...formData,
        updatedAt: new Date().toISOString()
      })
      await logAction('UPDATE', 'bible_school_classes', editingClass._id, `Turma atualizada: ${formData.title}`)
      resetForm()
      fetchData()
    } catch (error) {
      console.error('Erro ao atualizar turma:', error)
      alert('Erro ao atualizar turma')
    }
  }

  const handleDelete = async (cls: BibleSchoolClass) => {
    if (!confirm(`Confirma exclusão da turma "${cls.title}"?`)) return
    try {
      await deleteRecord(cls._id)
      await logAction('DELETE', 'bible_school_classes', cls._id, `Turma excluída: ${cls.title}`)
      fetchData()
    } catch (error) {
      console.error('Erro ao excluir turma:', error)
      alert('Erro ao excluir turma')
    }
  }

  const handleEdit = (cls: BibleSchoolClass) => {
    setEditingClass(cls)
    setFormData({
      title: cls.title,
      category: cls.category,
      teacher: cls.teacher,
      schedule: cls.schedule,
      description: cls.description || '',
      startDate: cls.startDate ? cls.startDate.split('T')[0] : '',
      endDate: cls.endDate ? cls.endDate.split('T')[0] : '',
      maxStudents: cls.maxStudents || 0,
      materialsUrl: cls.materialsUrl || ''
    })
    setIsCreating(true)
  }

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'ADULTS',
      teacher: '',
      schedule: '',
      description: '',
      startDate: '',
      endDate: '',
      maxStudents: 0,
      materialsUrl: ''
    })
    setEditingClass(null)
    setIsCreating(false)
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Escola Bíblica</h1>
            <p className="text-gray-600 mt-1">Gerenciar turmas e matrículas</p>
          </div>
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Nova Turma
          </button>
        </div>

        {/* Create/Edit Form */}
        {isCreating && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingClass ? 'Editar Turma' : 'Criar Nova Turma'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={editingClass ? handleUpdate : handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ADULTS">Adultos</option>
                    <option value="YOUTH">Jovens</option>
                    <option value="KIDS">Crianças</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Professor *</label>
                  <input
                    type="text"
                    required
                    value={formData.teacher}
                    onChange={(e) => setFormData({ ...formData, teacher: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horário *</label>
                  <input
                    type="text"
                    required
                    value={formData.schedule}
                    onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                    placeholder="Ex: Sábados às 14h"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Término</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Alunos</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxStudents}
                    onChange={(e) => setFormData({ ...formData, maxStudents: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Materiais</label>
                  <input
                    type="url"
                    value={formData.materialsUrl}
                    onChange={(e) => setFormData({ ...formData, materialsUrl: e.target.value })}
                    placeholder="Link para download de materiais"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingClass ? 'Atualizar Turma' : 'Criar Turma'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar turmas por título ou professor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              {['ALL', 'ADULTS', 'YOUTH', 'KIDS'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(cat as any)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    filterCategory === cat ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {cat === 'ALL' ? 'Todos' : getCategoryLabel(cat)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Classes List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Carregando turmas...</div>
          ) : filteredClasses.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Nenhuma turma encontrada</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Turma</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Professor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horário</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vagas</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredClasses.map((cls) => (
                    <tr key={cls._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{cls.title}</div>
                          {cls.description && (
                            <div className="text-sm text-gray-500 line-clamp-1">{cls.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(cls.category)}`}>
                          {getCategoryLabel(cls.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{cls.teacher}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Calendar size={16} className="text-gray-400" />
                          {cls.schedule}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {cls.maxStudents ? (
                          <div className="flex items-center gap-2 text-sm">
                            <Users size={16} className="text-gray-400" />
                            <span className="text-gray-700">Máx. {cls.maxStudents}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Ilimitado</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(cls)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(cls)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Turmas</p>
                <p className="text-2xl font-bold text-gray-900">{classes.length}</p>
              </div>
              <BookOpen className="text-blue-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Adultos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {classes.filter(c => c.category === 'ADULTS').length}
                </p>
              </div>
              <Users className="text-blue-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Jovens</p>
                <p className="text-2xl font-bold text-gray-900">
                  {classes.filter(c => c.category === 'YOUTH').length}
                </p>
              </div>
              <Users className="text-purple-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Crianças</p>
                <p className="text-2xl font-bold text-gray-900">
                  {classes.filter(c => c.category === 'KIDS').length}
                </p>
              </div>
              <Users className="text-green-600" size={32} />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
